import { IVisibleLogic, LoanStatusType } from 'config'
import { fillObject } from 'utils'

export interface ILoanActionConfigBase {
  tasks: Array<any>
  emails: Array<any>
  aiPrompts?: Array<any>
  /**
   * Minutes to wait before sending the emails configured on this action.
   * 0 / empty = send immediately. Uploads in the same window merge into one email.
   */
  emailsDelayMinutes?: number | null
  /**
   * Minutes to wait before sending “Send email for task” notifications.
   * 0 / empty = send immediately.
   */
  emailDelayMinutes?: number | null
}

export interface ILoanActionStatusChangeRule {
  id: number
  statusTo: LoanStatusType | null
  statusToRequireVerify?: boolean
  statusListBeforeToUpdate?: LoanStatusType[]
  productTypes?: Record<string, boolean>
  visibleLogic?: IVisibleLogic[][]
}

export interface ILoanSubmissionActionConfig extends ILoanActionConfigBase {
  statusTo?: LoanStatusType | null
  statusToRequireVerify?: boolean
  statusListBeforeToUpdate?: LoanStatusType[]
  statusChangeRules?: ILoanActionStatusChangeRule[]
}

export enum ActionType {
  LoanStructure = 'loanStructure',
  LoanApplication = 'loanApplication',
  LoanSubmission = 'loanSubmission',
  LoanSubmissionReviews = 'loanSubmissionReviews',
  LoanOrdinary = 'loanOrdinary',
  LoanStatus = 'loanStatus',
  SubStatus = 'subStatus',
  LoanServicingStatus = 'loanServicingStatus',
  LoanServicing = 'loanServicing',
  LoanAppraisal = 'loanAppraisal',
  LoanPayment = 'loanPayment',
  PostClosing = 'postClosing',
  InvestorPortal = 'investorPortal',
  BorrowerPortal = 'borrowerPortal',
  UserAccounts = 'userAccounts',
  BorrowingBase = 'borrowingBase',
}

export const actionTypeTitles = {
  [ActionType.LoanStructure]: 'Loan Structure Actions',
  [ActionType.LoanApplication]: 'Loan Application Actions',
  [ActionType.LoanSubmission]: 'Loan Submission Actions',
  [ActionType.LoanSubmissionReviews]: 'Loan Submission Review Actions',
  [ActionType.LoanOrdinary]: 'Loan Ordinary Actions',
  [ActionType.LoanStatus]: 'Loan Status Actions',
  [ActionType.SubStatus]: 'Sub Statuses',
  [ActionType.LoanServicingStatus]: 'Loan Servicing Status Actions',
  [ActionType.LoanServicing]: 'Servicing Actions',
  [ActionType.LoanAppraisal]: 'Loan Appraisal Actions',
  [ActionType.LoanPayment]: 'Loan Payment Actions for Invoice fee',
  [ActionType.PostClosing]: 'Post Closing Actions',
  [ActionType.InvestorPortal]: 'Investor Portal Actions',
  [ActionType.BorrowerPortal]: 'Borrower Portal Actions',
  [ActionType.UserAccounts]: 'User Account Actions',
  [ActionType.BorrowingBase]: 'Borrowing Base Actions',
}

export const actionTypeTitlePrefix: Record<string, string> = {
  [ActionType.LoanStatus]: 'Loan Status',
  [ActionType.SubStatus]: 'Sub Status',
}

export const isActionTypeHasStatusTo = (type: ActionType) =>
  ![
    ActionType.LoanSubmissionReviews,
    ActionType.LoanStatus,
    ActionType.SubStatus,
    ActionType.LoanServicingStatus,
    ActionType.LoanServicing,
    ActionType.InvestorPortal,
    ActionType.BorrowerPortal,
    ActionType.UserAccounts,
  ].includes(type)

const fullHeaders = ['No', 'Action', 'Loan Status To', 'Actions', 'Order']
const reviewHeaders = ['No', 'Action', 'Tasks', 'Actions']

export const actionTypeHeaders: Record<ActionType, string[]> = fillObject(
  Object.values(ActionType),
  (type: ActionType) => (isActionTypeHasStatusTo(type) ? fullHeaders : reviewHeaders),
)

export const isActionTypeForLoan = (type: ActionType) => ![ActionType.UserAccounts].includes(type)
